import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { fetchPlans } from "../../redux/phonepay/plansList";

import {
  initiatePayment,
  checkPaymentStatus,
  fetchUserSubscriptionDetails,
  clearUserSubscription,
} from "../../redux/phonepay/phonepePaymentSlice";

const PlanPayment = () => {
  const dispatch = useDispatch();
  useEffect(() => {
  dispatch(clearUserSubscription());
}, [dispatch]);
  const navigate = useNavigate();

  // ================= PLANS =================

  const { plans, loading: plansLoading } = useSelector(
    (state) => state.plans
  );

  // ================= PHONEPE =================

  const {
    loading: paymentLoading,
    subscriptionDetails,
    subscriptionLoading,
  } = useSelector((state) => state.phonepe);

  // ================= STATE =================

  const [selectedPlan, setSelectedPlan] = useState(null);

  const [usernameOrEmail, setUsernameOrEmail] = useState("");

  const [mobile, setMobile] = useState("");

  const [toast, setToast] = useState("");

  // ================= TOAST =================

  const showToast = (msg) => {
    setToast(msg);

    setTimeout(() => {
      setToast("");
    }, 2500);
  };

  // ================= FETCH PLANS =================

  useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  // =========================================================
  // FETCH USER SUBSCRIPTION
  // WHEN BOTH USERNAME/EMAIL + MOBILE ARE ENTERED
  // =========================================================

  useEffect(() => {
    const username = usernameOrEmail.trim();

    const phone = mobile.trim();

    // Don't call API until both fields are filled
    if (!username || !phone) {
      return;
    }

    // Mobile must contain 10 digits
    if (phone.length !== 10) {
      return;
    }

    // Small delay so API is not called immediately
    // while user is still typing
    const timer = setTimeout(async () => {
      try {
        const result = await dispatch(
          fetchUserSubscriptionDetails({
            usernameOrEmail: username,
            mobile: phone,
          })
        ).unwrap();

        console.log(
          "USER SUBSCRIPTION DETAILS:",
          result
        );

      } catch (error) {
        console.log(
          "SUBSCRIPTION DETAILS ERROR:",
          error
        );
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [usernameOrEmail, mobile, dispatch]);

  // =========================================================
  // RECOVER PAYMENT
  // =========================================================

  useEffect(() => {
    const savedTxn =
      localStorage.getItem("pendingTransactionId");

    if (savedTxn) {
      verifyPayment(savedTxn);
    }
  }, []);

  // =========================================================
  // VERIFY PAYMENT
  // =========================================================

  const verifyPayment = async (txnId) => {
    try {
      const result = await dispatch(
        checkPaymentStatus(txnId)
      ).unwrap();

      const state = result?.state;

      if (state === "COMPLETED") {
        showToast("Payment Successful 🎉");
      } else if (state === "FAILED") {
        showToast("Payment Failed ❌");
      } else {
        showToast("Payment Pending ⏳");
      }

      navigate(`/payment-status/${txnId}`);
    } catch (err) {
      console.log("VERIFY ERROR:", err);

      showToast("Unable to verify payment");
    }
  };

  // =========================================================
  // INITIATE PAYMENT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !usernameOrEmail.trim() ||
      !mobile.trim() ||
      !selectedPlan
    ) {
      showToast("Please fill all fields");

      return;
    }

    try {
      const result = await dispatch(
        initiatePayment({
          usernameOrEmail:
            usernameOrEmail.trim(),

          mobile: mobile.trim(),

          planId: selectedPlan.planId,
        })
      ).unwrap();

      const txnId =
        result?.transactionId;

      // =====================================================
      // PAYMENT ALREADY IN PROGRESS
      // =====================================================

      if (
        result?.message ===
        "Payment already in progress"
      ) {
        showToast(
          "Payment already in progress"
        );

        if (txnId) {
          localStorage.setItem(
            "pendingTransactionId",
            txnId
          );

          navigate(
            `/payment-status/${txnId}`
          );
        }

        return;
      }

      // =====================================================
      // NORMAL PAYMENT
      // =====================================================

      if (
        result?.paymentUrl &&
        txnId
      ) {
        localStorage.setItem(
          "pendingTransactionId",
          txnId
        );

        window.location.href =
          result.paymentUrl;

        return;
      }

      showToast(
        "Payment initiation failed"
      );

    } catch (err) {
      console.log(
        "INIT ERROR:",
        err
      );

      const data =
        err?.payload ||
        err?.response?.data ||
        err;

      const txnId =
        data?.transactionId;

      const message =
        data?.message;

      if (
        txnId &&
        message ===
        "Payment already in progress"
      ) {
        showToast(
          "Payment already in progress"
        );

        localStorage.setItem(
          "pendingTransactionId",
          txnId
        );

        navigate(
          `/payment-status/${txnId}`
        );

        return;
      }

      showToast(
        message ||
        "Server error"
      );
    }
  };

  // =========================================================
  // GST
  // =========================================================

  const getGST = (plan) => {
    if (!plan) {
      return {
        base: 0,
        gst: 0,
        total: 0,
      };
    }

    const total =
      plan.totalPrice || 0;

    const gst =
      plan.gstAmount ||
      Math.round(
        (total * 18) / 118
      );

    const base =
      total - gst;

    return {
      base,
      gst,
      total,
    };
  };

  const gst =
    getGST(selectedPlan);

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex justify-center p-6">

      {/* ===================================================
          TOAST
      =================================================== */}

      {toast && (
        <div className="fixed top-5 right-5 bg-black text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {toast}
        </div>
      )}

      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-xl p-8">

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="text-center mb-8">

          <h2 className="text-3xl font-bold">
            Subscription Checkout ⚡
          </h2>

          <p className="text-gray-500">
            Fast & secure payment system
          </p>

        </div>

        <form
          onSubmit={handleSubmit}
          className="grid md:grid-cols-3 gap-6"
        >

          {/* =================================================
              LEFT SIDE
          ================================================= */}

          <div className="md:col-span-2">

            {/* USERNAME / EMAIL */}

            <input
              type="text"
              className="border p-3 rounded-lg w-full mb-3"
              placeholder="Username / Email"
              value={usernameOrEmail}
              onChange={(e) =>
                setUsernameOrEmail(
                  e.target.value
                )
              }
            />

            {/* MOBILE */}

            <input
              type="tel"
              maxLength={10}
              className="border p-3 rounded-lg w-full mb-4"
              placeholder="Mobile"
              value={mobile}
              onChange={(e) => {

                const value =
                  e.target.value.replace(
                    /\D/g,
                    ""
                  );

                setMobile(value);
              }}
            />

            {/* =================================================
                SUBSCRIPTION LOADING
            ================================================= */}

            {subscriptionLoading && (
              <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700">

                Checking user's
                subscription...

              </div>
            )}

            {/* =================================================
                USER SUBSCRIPTION DETAILS
            ================================================= */}

            {subscriptionDetails?.subscription && (
  <div className="mb-6 p-5 rounded-xl border border-green-200 bg-white shadow-sm">

    {/* Header */}
    <div className="flex items-center justify-between mb-5">
      <div>
        <h3 className="text-lg font-bold text-gray-800">
          Current Subscription
        </h3>
        <p className="text-sm text-gray-500">
          Your active subscription details
        </p>
      </div>

      <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
        {subscriptionDetails.subscription.status
          ?.toUpperCase()}
      </span>
    </div>

    {/* Plan */}
    <div className="bg-blue-50 rounded-xl p-4 mb-4">
      <p className="text-sm text-gray-500">
        Current Plan
      </p>

      <p className="text-xl font-bold text-blue-700 mt-1">
        {subscriptionDetails.subscription.plan}
      </p>
    </div>

    {/* Details */}
    <div className="grid md:grid-cols-2 gap-4 text-sm">

      <div>
        <p className="text-gray-500">Subscription Type</p>
        <p className="font-semibold text-gray-800">
          {subscriptionDetails.subscription.type}
        </p>
      </div>

      <div>
        <p className="text-gray-500">Amount Paid</p>
        <p className="font-semibold text-gray-800">
          ₹{(
            subscriptionDetails.subscription.amountPaise / 100
          ).toLocaleString("en-IN")}
        </p>
      </div>

      <div>
        <p className="text-gray-500">Start Date</p>
        <p className="font-semibold text-gray-800">
          {new Date(
            subscriptionDetails.subscription.startDate
          ).toLocaleDateString("en-IN")}
        </p>
      </div>

      <div>
        <p className="text-gray-500">Expiry Date</p>
        <p className="font-semibold text-gray-800">
          {new Date(
            subscriptionDetails.subscription.endDate
          ).toLocaleDateString("en-IN")}
        </p>
      </div>

    </div>

    {/* Days Remaining */}
    <div className="mt-5 pt-4 border-t flex items-center justify-between">

      <span className="text-gray-600 font-medium">
        Days Remaining
      </span>

      <span className="text-lg font-bold text-green-600">
        {subscriptionDetails.subscription.daysLeft} days
      </span>

    </div>

  </div>
)}

            {/* =================================================
                CHOOSE PLAN
            ================================================= */}

            <h3 className="font-semibold mb-3">
              Choose Plan 🚀
            </h3>

            {plansLoading ? (

              <p>
                Loading plans...
              </p>

            ) : (

              <div className="grid md:grid-cols-2 gap-4">

                {(plans || []).map(
                  (plan) => (

                    <div
                      key={plan.planId}
                      onClick={() =>
                        setSelectedPlan(
                          plan
                        )
                      }
                      className={`p-5 border rounded-xl cursor-pointer transition hover:scale-105 ${selectedPlan?.planId ===
                          plan.planId
                          ? "border-green-500 bg-green-50"
                          : ""
                        }`}
                    >

                      <h4 className="font-bold">
                        {plan.planName}
                      </h4>

                      <p className="text-green-600 font-semibold">
                        ₹
                        {
                          plan.totalPrice
                        }
                      </p>

                    </div>

                  )
                )}

              </div>

            )}

          </div>

          {/* =================================================
              RIGHT SIDE
          ================================================= */}

          <div className="bg-gray-50 p-5 rounded-xl border">

            <h3 className="font-bold mb-4">
              Invoice Summary 📄
            </h3>

            {!selectedPlan ? (

              <p>
                Select a plan to continue
              </p>

            ) : (

              <div className="space-y-2 text-sm">

                <p>
                  <b>Plan:</b>{" "}
                  {
                    selectedPlan.planName
                  }
                </p>

                <p>
                  <b>Base:</b>{" "}
                  ₹{gst.base}
                </p>

                <p>
                  <b>GST:</b>{" "}
                  ₹{gst.gst}
                </p>

                <p className="text-green-700 font-bold text-lg">
                  Total: ₹
                  {gst.total}
                </p>

                <hr />

                <p>
                  <b>User:</b>{" "}
                  {usernameOrEmail ||
                    "-"}
                </p>

                <p>
                  <b>Mobile:</b>{" "}
                  {mobile || "-"}
                </p>

              </div>

            )}

            {/* =================================================
                PAY BUTTON
            ================================================= */}

            <button
              type="submit"
              disabled={
                paymentLoading ||
                !selectedPlan ||
                !usernameOrEmail.trim() ||
                mobile.length !== 10
              }
              className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold disabled:bg-gray-400"
            >
              {paymentLoading
                ? "Processing..."
                : "Pay & Continue ⚡"}
            </button>

          </div>

        </form>

      </div>

    </div>
  );
};

export default PlanPayment;
// import React, { useEffect, useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";

// import { fetchPlans } from "../../redux/phonepay/plansList";
// import {
//   initiatePayment,
//   checkPaymentStatus,
//   fetchUserSubscriptionDetails,
// } from "../../redux/phonepay/phonepePaymentSlice";

// const PlanPayment = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const { plans, loading: plansLoading } = useSelector(
//     (state) => state.plans
//   );

//   const { loading: paymentLoading } = useSelector(
//     (state) => state.phonepe
//   );

//   const [selectedPlan, setSelectedPlan] = useState(null);
//   const [usernameOrEmail, setUsernameOrEmail] = useState("");
//   const [mobile, setMobile] = useState("");
//   const [toast, setToast] = useState("");

//   const showToast = (msg) => {
//     setToast(msg);
//     setTimeout(() => setToast(""), 2500);
//   };

//   // ================= FETCH PLANS =================
//   useEffect(() => {
//     dispatch(fetchPlans());
//   }, [dispatch]);

//   // ================= RECOVER PAYMENT =================
//   useEffect(() => {
//     const savedTxn = localStorage.getItem("pendingTransactionId");

//     if (savedTxn) {
//       verifyPayment(savedTxn);
//     }
//   }, []);

//   // ================= VERIFY PAYMENT =================
//   const verifyPayment = async (txnId) => {
//     try {
//       const result = await dispatch(
//         checkPaymentStatus(txnId)
//       ).unwrap();

//       const state = result?.state;

//       if (state === "COMPLETED") {
//         showToast("Payment Successful 🎉");
//       } else if (state === "FAILED") {
//         showToast("Payment Failed ❌");
//       } else {
//         showToast("Payment Pending ⏳");
//       }

//       navigate(`/payment-status/${txnId}`);
//     } catch (err) {
//       console.log("VERIFY ERROR:", err);
//       showToast("Unable to verify payment");
//     }
//   };

//   // ================= INITIATE PAYMENT =================
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!usernameOrEmail || !mobile || !selectedPlan) {
//       showToast("Please fill all fields");
//       return;
//     }

//     try {
//       const result = await dispatch(
//         initiatePayment({
//           usernameOrEmail,
//           mobile,
//           planId: selectedPlan.planId,
//         })
//       ).unwrap();

//       const txnId = result?.transactionId;

//       // ================= ALREADY IN PROGRESS =================
//       if (result?.message === "Payment already in progress") {
//         showToast("Payment already in progress");
//         localStorage.setItem("pendingTransactionId", txnId);
//         navigate(`/payment-status/${txnId}`);
//         return;
//       }

//       // ================= NORMAL FLOW =================
//       if (result?.paymentUrl && txnId) {
//         localStorage.setItem("pendingTransactionId", txnId);
//         window.location.href = result.paymentUrl;
//         return;
//       }

//       showToast("Payment initiation failed");
//     } catch (err) {
//       console.log("INIT ERROR:", err);

//       const data = err?.payload || err?.response?.data || err;

//       const txnId = data?.transactionId;
//       const message = data?.message;

//       if (txnId && message === "Payment already in progress") {
//         showToast("Payment already in progress");
//         localStorage.setItem("pendingTransactionId", txnId);
//         navigate(`/payment-status/${txnId}`);
//         return;
//       }

//       showToast(message || "Server error");
//     }
//   };

//   // ================= GST =================
//   const getGST = (plan) => {
//     if (!plan) return { base: 0, gst: 0, total: 0 };

//     const total = plan.totalPrice || 0;
//     const gst = plan.gstAmount || Math.round((total * 18) / 118);
//     const base = total - gst;

//     return { base, gst, total };
//   };

//   const gst = getGST(selectedPlan);

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex justify-center p-6">

//       {/* TOAST */}
//       {toast && (
//         <div className="fixed top-5 right-5 bg-black text-white px-4 py-2 rounded-lg shadow-lg z-50">
//           {toast}
//         </div>
//       )}

//       <div className="w-full max-w-6xl bg-white rounded-2xl shadow-xl p-8">

//         {/* HEADER */}
//         <div className="text-center mb-8">
//           <h2 className="text-3xl font-bold">Subscription Checkout ⚡</h2>
//           <p className="text-gray-500">Fast & secure payment system</p>
//         </div>

//         <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-6">

//           {/* LEFT */}
//           <div className="md:col-span-2">

//             <input
//               className="border p-3 rounded-lg w-full mb-3"
//               placeholder="Username / Email"
//               value={usernameOrEmail}
//               onChange={(e) => setUsernameOrEmail(e.target.value)}
//             />

//             <input
//               className="border p-3 rounded-lg w-full mb-4"
//               placeholder="Mobile"
//               value={mobile}
//               onChange={(e) => setMobile(e.target.value)}
//             />

//             <h3 className="font-semibold mb-3">Choose Plan 🚀</h3>

//             {plansLoading ? (
//               <p>Loading plans...</p>
//             ) : (
//               <div className="grid md:grid-cols-2 gap-4">
//                 {(plans || []).map((plan) => (
//                   <div
//                     key={plan.planId}
//                     onClick={() => setSelectedPlan(plan)}
//                     className={`p-5 border rounded-xl cursor-pointer transition hover:scale-105 ${
//                       selectedPlan?.planId === plan.planId
//                         ? "border-green-500 bg-green-50"
//                         : ""
//                     }`}
//                   >
//                     <h4 className="font-bold">{plan.planName}</h4>
//                     <p className="text-green-600 font-semibold">
//                       ₹{plan.totalPrice}
//                     </p>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* RIGHT */}
//           <div className="bg-gray-50 p-5 rounded-xl border">

//             <h3 className="font-bold mb-4">Invoice Summary 📄</h3>

//             {!selectedPlan ? (
//               <p>Select a plan to continue</p>
//             ) : (
//               <div className="space-y-2 text-sm">

//                 <p><b>Plan:</b> {selectedPlan.planName}</p>
//                 <p><b>Base:</b> ₹{gst.base}</p>
//                 <p><b>GST:</b> ₹{gst.gst}</p>

//                 <p className="text-green-700 font-bold text-lg">
//                   Total: ₹{gst.total}
//                 </p>

//                 <hr />

//                 <p><b>User:</b> {usernameOrEmail || "-"}</p>
//                 <p><b>Mobile:</b> {mobile || "-"}</p>

//               </div>
//             )}

//             <button
//               disabled={paymentLoading || !selectedPlan}
//               className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold disabled:bg-gray-400"
//             >
//               {paymentLoading ? "Processing..." : "Pay & Continue ⚡"}
//             </button>

//           </div>

//         </form>
//       </div>
//     </div>
//   );
// };

// export default PlanPayment;